import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Copy } from "lucide-react";
import { api } from "../lib/api.js";
import { Button, Input, Mono, Spinner } from "./ui.jsx";

// Render-style two-step wizard: choose a hostname, then show the exact DNS records
// selected by the server (root plus www, or one exact subdomain). Saving happens on step 1
// (POST /domain): if DNS already points here the domain is bound and deployed; otherwise it
// is saved as waiting and bound automatically once DNS is correct. Step 2 shows the records
// from GET /domain/records — the single source of truth — and lets the user verify propagation.
export default function AddDomainModal({ serviceId, onClose, onBound }) {
  const [step, setStep] = useState(1);
  const [domain, setDomain] = useState("");
  const [addedHosts, setAddedHosts] = useState([]);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const hostname = domain.trim();

  async function addDomain(e) {
    e?.preventDefault();
    if (!hostname || busy) return;
    setBusy(true); setErr(null);
    try {
      const data = await api.addDomain(serviceId, hostname);
      const hosts = (data?.addedDomains || []).map(hostOf).filter(Boolean);
      setAddedHosts(hosts.length ? hosts : [hostOf(hostname) || hostname]);
      setPending(!!data?.pending);
      onBound?.();
      setStep(2);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(560px, 94vw)", maxHeight: "92vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--text)" }}>Add Custom Domain</h3>
          <button onClick={onClose} title="Close" style={{ color: "var(--text-muted)" }}><X className="h-4 w-4" /></button>
        </div>

        {/* step tabs */}
        <div className="mb-4 flex gap-4 text-[13px]">
          <StepTab n={1} label="Choose domain name" active={step === 1} done={step > 1} />
          <StepTab n={2} label="Add DNS records" active={step === 2} done={false} />
        </div>

        {step === 1 && (
          <form onSubmit={addDomain}>
            <p className="mb-2 text-[12.5px]" style={{ color: "var(--text-muted)" }}>
              Enter the hostname you want to point at this service. A root domain gets a <Mono>www</Mono> alias; a subdomain such as <Mono>app.example.com</Mono> is added on its own.
            </p>
            <Input autoFocus value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com or app.example.com" style={{ width: "100%", fontFamily: "monospace" }} />
            {err && <p className="mt-2 text-xs" style={{ color: "var(--err-text)" }}>{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" disabled={!hostname || busy}>{busy ? <Spinner /> : "Add Domain"}</Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <Step2 hosts={addedHosts} pending={pending} serviceId={serviceId} onClose={onClose} onVerified={onBound} />
        )}
      </div>
    </div>
  );
}

// "https://example.com/" or "Example.com" → "example.com"; null when it isn't a hostname.
function hostOf(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  try { return new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`).hostname.toLowerCase() || null; }
  catch { return null; }
}

function StepTab({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-1.5" style={{ color: active || done ? "var(--text)" : "var(--text-muted)", opacity: active || done ? 1 : 0.6 }}>
      <span
        className="grid h-5 w-5 place-items-center rounded-full text-[11px]"
        style={{ background: active ? "var(--accent)" : "var(--surface-2)", color: active ? "#fff" : "var(--text-muted)", border: "1px solid var(--border)" }}
      >
        {done ? <Check className="h-3 w-3" /> : n}
      </span>
      <span className="font-medium">{label}</span>
    </div>
  );
}

function Step2({ hosts, pending, serviceId, onClose, onVerified }) {
  const [records, setRecords] = useState(null); // null = loading
  const [recErr, setRecErr] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [results, setResults] = useState(null); // { host: { ok, ips, expected } }
  const [verifying, setVerifying] = useState(false);

  // One request covers a root + www pair: ask for the root and the server returns both rows.
  const recordsFor = (hosts.length > 1 && hosts.find((h) => !h.startsWith("www."))) || hosts[0];

  useEffect(() => {
    if (!recordsFor) return;
    let live = true;
    setRecords(null); setRecErr(null);
    api.domainRecords(serviceId, recordsFor)
      .then((d) => { if (live) setRecords(Array.isArray(d?.records) ? d.records : []); })
      .catch((e) => { if (live) { setRecords([]); setRecErr(e.message || "Couldn't load the DNS records."); } });
    return () => { live = false; };
  }, [serviceId, recordsFor, attempt]);

  async function verify() {
    setVerifying(true);
    const out = {};
    for (const host of hosts) {
      try {
        const d = await api.verifyDomain(serviceId, host);
        out[host] = { ok: !!d.pointsToServer, ips: d.resolvedIps || [], expected: d.expectedIp };
      } catch (e) { out[host] = { ok: false, error: e.message }; }
    }
    setResults(out); setVerifying(false);
    // DNS now points here → the list behind this modal has news (a waiting domain gets bound).
    if (Object.values(out).some((r) => r.ok)) onVerified?.();
  }

  return (
    <div>
      {pending && (
        <div className="mb-3 rounded-md border p-2.5 text-[12.5px]" style={{ background: "var(--warn-soft)", borderColor: "var(--warn)", color: "var(--warn-text)" }}>
          Saved — we'll connect it automatically as soon as DNS points here.
        </div>
      )}
      <p className="mb-3 text-[12.5px]" style={{ color: "var(--text-muted)" }}>
        Add {records?.length === 1 ? "this record" : "these records"} at your DNS provider, then verify. TLS is issued automatically once DNS points here.
      </p>

      {records === null && <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--text-muted)" }}><Spinner /> Loading DNS records…</div>}
      {records?.map((r, i) => <Record key={`${r.type}-${r.name}-${i}`} n={i + 1} record={r} />)}
      {records && !records.length && (
        <div className="rounded-md border p-2.5 text-[12px]" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-muted)" }}>
          {recErr || "No DNS records were returned for this domain."} You can also find them under <b>Or add records manually</b> on the domain's row once you close this.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)} style={{ color: "var(--accent-text)" }}>Try again</button>
        </div>
      )}

      {results && (
        <div className="mt-3 flex flex-col gap-1">
          {hosts.map((host) => <VerifyLine key={host} host={host} status={results[host]} />)}
        </div>
      )}

      <p className="mt-3 text-[11.5px]" style={{ color: "var(--text-muted)" }}>ℹ DNS changes can take up to 24 hours to propagate.</p>
      <div className="mt-4 flex items-center justify-between">
        <Link to="/docs/custom-domains" target="_blank" className="text-[12.5px]" style={{ color: "var(--accent-text)" }}>Read the docs →</Link>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="default" onClick={verify} disabled={verifying || !hosts.length}>{verifying ? <Spinner /> : "Verify"}</Button>
        </div>
      </div>
    </div>
  );
}

function Record({ n, record }) {
  return (
    <div className="mb-2 rounded-lg border p-3" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
      <div className="mb-1.5 text-[12px] font-semibold" style={{ color: "var(--text)" }}>{n}. {record.type} record</div>
      <div className="grid grid-cols-[0.5fr_1fr_1.4fr] gap-3 text-[12px]">
        <Cell label="Type" value={record.type} />
        <Cell label="Name" value={record.name} />
        <Cell label="Value" value={record.value} />
      </div>
      {record.note && <p className="mt-1.5 text-[11.5px]" style={{ color: "var(--text-muted)" }}>{record.note}</p>}
    </div>
  );
}

function VerifyLine({ host, status }) {
  if (!status) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
      <span className="mono" style={{ color: "var(--text)" }}>{host}</span>
      {status.ok
        ? <span className="inline-flex items-center gap-1" style={{ color: "var(--ok-text)" }}><Check className="h-3.5 w-3.5" strokeWidth={3} /> verified</span>
        : <span className="inline-flex items-center gap-1" style={{ color: "var(--warn-text)" }}>
            <X className="h-3.5 w-3.5" />
            {status.error
              ? status.error
              : `${status.ips?.length ? `resolves to ${status.ips.join(", ")}` : "not resolving yet"}${status.expected ? ` — expected ${status.expected}` : ""}`}
          </span>}
    </div>
  );
}

function Cell({ label, value }) {
  const text = value == null ? "" : String(value);
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[10.5px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</p>
      <CopyInline text={text}>
        <span className="mono block min-w-0 flex-1 truncate rounded border px-2 py-1.5" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--text)" }}>{text}</span>
      </CopyInline>
    </div>
  );
}

function CopyInline({ text, children }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => text && navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      title={copied ? "Copied" : "Copy"}
      className="inline-flex w-full items-center gap-1 text-left"
      style={{ color: copied ? "var(--ok-text)" : "inherit" }}
    >
      {children}
      {copied ? <Check className="h-3 w-3 shrink-0" style={{ color: "var(--ok-text)" }} /> : <Copy className="h-3 w-3 shrink-0" style={{ color: "var(--text-muted)" }} />}
    </button>
  );
}
